import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'
import { ArrowDown, Move, Users } from 'react-feather'

import { Props as ServiceProps, Service } from './Service'

export interface Props {
  claim?: string
  image?: ImageProps
  pax?: string
  services?: ServiceProps[]
  size?: string
  variant?: Variant
}

export const Hero = memo(function Hero({
  claim,
  image,
  pax,
  services,
  size,
  variant = 'default',
}: Props) {
  const onClick = () => {
    window.scroll({
      top: window.innerHeight + 1,
      left: 0,
      behavior: 'smooth',
    })
  }

  return (
    <Container
      className={size || pax ? 'advanced' : undefined}
      variant={variant}
    >
      {image ? <Background {...image} /> : null}
      {claim ? <Claim variant={variant}>{claim}</Claim> : null}
      {variant === 'default' ? (
        <ButtonScroll dial={5} onClick={onClick}>
          <ArrowDown />
        </ButtonScroll>
      ) : (
        <Info dial={5} row wrap>
          {size ? (
            <Item>
              <Move />
              <Label>{size}</Label>
            </Item>
          ) : null}
          {pax ? (
            <Item>
              <Users />
              <Label>{pax}</Label>
            </Item>
          ) : null}
          {services
            ? services.map((item, index) => <Service key={index} {...item} />)
            : null}
        </Info>
      )}
    </Container>
  )
})

const Container = styled.section<ContainerProps>`
  height: 100vh;
  max-height: -webkit-fill-available;
  position: relative;
  &:before,
  &:after {
    content: '';
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }
  &:before {
    height: 50%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark1, 0.42)},
      ${rgba(theme.colors.variants.neutralDark1, 0)}
    );
  }
  &:after {
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.4;
  }

  @media (max-width: 1023px) {
    &.advanced {
      height: auto;
      > img {
        height: 80vh;
        position: relative;
      }
      > h1 {
        top: calc(50% - 10vh);
      }
    }
  }

  ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return css`
          height: 62vh;
        `
    }
  }}
`

const Background = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`

const Claim = styled.h1<ContainerProps>`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 3.125rem;
  font-weight: 200;
  letter-spacing: 0.3125rem;
  line-height: 3.75rem;
  opacity: 0.6;
  position: absolute;
  top: 50%;
  right: 5.5rem;
  left: 5.5rem;
  text-align: center;
  text-transform: uppercase;
  transform: translateY(-50%);
  z-index: 3;

  @media (max-width: 1199px) {
    font-size: 2.5rem;
    letter-spacing: 0.25rem;
    line-height: 3rem;
    right: 8.454vw;
    left: 8.454vw;
  }

  ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return css`
          top: calc(50% + 7vh);
        `
    }
  }}
`

const ButtonScroll = styled(FlexBox)`
  width: 5.25rem;
  height: 5.25rem;
  border: 2px solid ${rgba(theme.colors.variants.neutralLight4, 0.5)};
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  bottom: 2.875rem;
  left: 50%;
  transform: translateX(-50%);
  transition: 0.3s ease-in-out;
  z-index: 3;
  &:hover {
    background: ${rgba(theme.colors.variants.neutralLight4, 0.3)};
    border-color: transparent;
  }
  svg {
    width: auto;
    height: 2.5rem;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
    stroke-width: 1;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Info = styled(FlexBox)`
  position: absolute;
  bottom: 5.25rem;
  right: 1.5rem;
  left: 1.5rem;
  z-index: 3;

  @media (max-width: 1023px) {
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    padding: 2.5rem 8.454vw;
    position: relative;
    bottom: auto;
    right: auto;
    left: auto;
  }
`

const Item = styled.div`
  margin: 0 1.875rem;
  text-align: center;
  svg {
    display: block;
    width: auto;
    height: 1.5rem;
    opacity: 0.4;
    margin: auto;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
    stroke-width: 1.5px;
  }

  @media (max-width: 767px) {
    width: 50%;
    justify-content: space-between;
    margin: 0 auto;
  }
`

const Label = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.9375rem;
  line-height: 1.875rem;
  opacity: 0.6;
  margin-top: 1.5rem;
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'advanced' | 'compact'
